import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, SalesBadge } from "@/components/common/StatusBadge";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { Building2, FileCheck2, AlertTriangle, TrendingDown, Loader2, CalendarX, Unlink } from "lucide-react";
import CityBreakdown from "@/components/analytics/CityBreakdown";

const PIE = ["#1E3A8A", "#94A3B8"];
const BAR = "#1E3A8A";

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${accent}`}><Icon className="h-5 w-5" /></div>
      </div>
      <div className="mt-3 text-3xl font-mono font-bold">{value}</div>
      <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{label}</div>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [states, setStates] = useState([]);
  const [state, setState] = useState("");

  const load = (st) => {
    setLoading(true);
    const params = {};
    if (st) params.state = st;
    api.get("/analytics", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };

  useEffect(() => { api.get("/locations/states").then((r) => setStates(r.data.states)); }, []);
  useEffect(() => { load(state); }, [state]);

  if (loading || !data) return <div className="h-80 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const pieData = [
    { name: "Contracted", value: data.contracted },
    { name: "Non-Contracted", value: data.non_contracted },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight">Management Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Active month: {data.month}</p>
        </div>
        <Select value={state || "__all"} onValueChange={(v) => setState(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-[180px]" data-testid="analytics-state-filter"><SelectValue placeholder="All States" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">All States</SelectItem>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi icon={Building2} label="Total Corporates" value={data.total} accent="bg-primary/10 text-primary" testid="analytics-kpi-total-corporates" />
        <Kpi icon={FileCheck2} label="Contracted" value={`${data.contracted}${data.total ? ` · ${Math.round((data.contracted / data.total) * 100)}%` : ""}`} accent="bg-blue-50 text-blue-700" testid="analytics-kpi-contracted" />
        <Kpi icon={Unlink} label="Non-Contracted" value={data.non_contracted} accent="bg-slate-100 text-slate-600" testid="analytics-kpi-non-contracted" />
        <Kpi icon={AlertTriangle} label="Expiring / Expired (30d)" value={data.expiring_count} accent="bg-orange-50 text-orange-700" testid="analytics-kpi-expiring" />
        <Kpi icon={TrendingDown} label="No Sales This Month" value={data.no_sales_count} accent="bg-rose-50 text-rose-700" testid="analytics-kpi-no-sales" />
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 rounded-xl border border-border bg-card p-5">
          <h3 className="text-base font-semibold mb-4">Corporates by District</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.by_district.slice(0, 8)} margin={{ left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill={BAR} radius={[4, 4, 0, 0]} name="Corporates" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
          <h3 className="text-base font-semibold mb-4">Contracted vs Non-Contracted</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={3}>
                {pieData.map((_, i) => <Cell key={i} fill={PIE[i]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <CityBreakdown rows={data.by_city || []} />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="analytics-expiring-list">
          <div className="px-5 py-3.5 border-b border-border flex items-center gap-2 bg-orange-50/50">
            <CalendarX className="h-4 w-4 text-orange-700" /> <h3 className="text-base font-semibold">Expiring / Expired Agreements (30 days)</h3>
          </div>
          <div className="divide-y divide-border max-h-80 overflow-y-auto">
            {data.expiring.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">None expiring soon.</div> :
              data.expiring.map((c) => (
                <div key={c.id} className="px-5 py-3 flex items-center justify-between">
                  <div><div className="text-sm font-medium">{c.company_name}</div><div className="text-xs text-muted-foreground">{c.district} · ends {c.agreement_end}</div></div>
                  <StatusBadge status={c.contract_status} />
                </div>
              ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="analytics-no-sales-list">
          <div className="px-5 py-3.5 border-b border-border flex items-center gap-2 bg-rose-50/50">
            <TrendingDown className="h-4 w-4 text-rose-700" /> <h3 className="text-base font-semibold">No-Sales Corporates ({data.month})</h3>
          </div>
          <div className="divide-y divide-border max-h-80 overflow-y-auto">
            {data.no_sales.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Every corporate has sales. 🎉</div> :
              data.no_sales.map((c) => (
                <div key={c.id} className="px-5 py-3 flex items-center justify-between">
                  <div><div className="text-sm font-medium">{c.company_name}</div><div className="text-xs text-muted-foreground">{c.district} · {c.poc_name}</div></div>
                  <SalesBadge yes={false} />
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
